import { Injectable } from '@angular/core';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';

import * as StocksActions from './stocks.actions';
import { StocksService } from '../../services/stocks.service';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { MessageService } from 'primeng/api';

@Injectable({
  providedIn: 'root',
})
export class StocksEffects {
  loadStocks$: any;
  loadTemplates$: any;
  loadTemplateProperties$: any;
  loadStock$: any;
  createStock$: any;
  updateStock$: any;
  deleteStock$: any;
  activateStock$: any;
  deactivateStock$: any;
  loadSectors$: any;
  loadMarkets$: any;
  loadIndustries$: any;

  constructor(
    private actions$: Actions,
    private stocksService: StocksService,
    private router: Router,
    private messageService: MessageService
  ) {
    // Load Stocks - Updated to handle new response structure
    this.loadStocks$ = createEffect(() =>
      this.actions$.pipe(
        ofType(StocksActions.loadStocks),
        switchMap(({ filters }) =>
          this.stocksService
            .getAllStocks(
              filters?.pageNumber || 1,
              filters?.pageSize || 20,
              filters?.searchTerm || '',
              filters?.sectorId
            )
            .pipe(
              map((response) => {
                // Extract stocks from the new response structure
                const stocks = response.data?.items || [];
                return StocksActions.loadStocksSuccess({
                  stocks: stocks,
                  pagination: {
                    currentPage: response.data?.pageNumber || 1,
                    pageSize: response.data?.pageSize || 20,
                    totalItems: response.data?.totalCount || 0,
                  },
                });
              }),
              catchError((error) => {
                console.error('Error loading stocks:', error);
                return of(
                  StocksActions.loadStocksFailure({
                    error: error.message || 'Failed to load stocks. Please try again.',
                  })
                );
              })
            )
        )
      )
    );

    // Fixed loadTemplates$ effect - removed duplicate
    this.loadTemplates$ = createEffect(() =>
      this.actions$.pipe(
        ofType(StocksActions.loadTemplates),
        mergeMap(() => {
          return this.stocksService.getTemplates().pipe(
            map((response) => {
              // Extract the data array from the response
              const templates = response.data || [];
              return StocksActions.loadTemplatesSuccess({ templates });
            }),
            catchError((error) => of(StocksActions.loadTemplatesFailure({ error: error.message })))
          );
        })
      )
    );

    // Load Single Stock
    this.loadStock$ = createEffect(() =>
      this.actions$.pipe(
        ofType(StocksActions.loadStock),
        mergeMap(({ id }) =>
          this.stocksService.getStockById(id).pipe(
            map((response) => {
              // Extract stock from response if needed
              const stock = response.data || [];
              return StocksActions.loadStockSuccess({ stock });
            }),
            catchError((error) => of(StocksActions.loadStockFailure({ error: error.message })))
          )
        )
      )
    );

    // Create Stock
    this.createStock$ = createEffect(() =>
      this.actions$.pipe(
        ofType(StocksActions.createStock),
        switchMap(({ stock }) =>
          this.stocksService.createStock(stock).pipe(
            map((response) => {
              // Extract created stock from response if needed
              const createdStock = response.data.items || [];
              // Show success message
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Company created successfully',
              });
              // TODO this.router.navigate(['/stocks']);
              this.router.navigate(['/']);
              return StocksActions.createStockSuccess({ stock: createdStock });
            }),
            catchError((error) => {
              return of(StocksActions.createStockFailure({ error: error.message }));
            })
          )
        )
      )
    );

    // Update Stock
    this.updateStock$ = createEffect(() =>
      this.actions$.pipe(
        ofType(StocksActions.updateStock),
        switchMap(({ stockId, stock }) =>
          this.stocksService.updateStock(stockId, stock).pipe(
            map((response) => {
              // Extract updated stock from response if needed
              const updatedStock = response.data || [];
              return StocksActions.updateStockSuccess({ stock: updatedStock });
            }),
            catchError((error) => of(StocksActions.updateStockFailure({ error: error.message })))
          )
        )
      )
    );

    // Delete Stock
    this.deleteStock$ = createEffect(() =>
      this.actions$.pipe(
        ofType(StocksActions.deleteStock),
        mergeMap(({ id }) =>
          this.stocksService.deleteStock(id).pipe(
            map(() => StocksActions.deleteStockSuccess({ id })),
            catchError((error) => of(StocksActions.deleteStockFailure({ error: error.message })))
          )
        )
      )
    );

    // Activate Stock
    this.activateStock$ = createEffect(() =>
      this.actions$.pipe(
        ofType(StocksActions.activateStock),
        mergeMap(({ id }) =>
          this.stocksService.activateStock(id).pipe(
            map(() => {
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Stock activated successfully',
              });
              return StocksActions.activateStockSuccess({ id });
            }),
            catchError((error) => of(StocksActions.activateStockFailure({ error: error.message })))
          )
        )
      )
    );

    // Deactivate Stock
    this.deactivateStock$ = createEffect(() =>
      this.actions$.pipe(
        ofType(StocksActions.deactivateStock),
        mergeMap(({ id }) =>
          this.stocksService.deactivateStock(id).pipe(
            map(() => {
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Stock deactivated successfully',
              });
              return StocksActions.deactivateStockSuccess({ id });
            }),
            catchError((error) =>
              of(StocksActions.deactivateStockFailure({ error: error.message }))
            )
          )
        )
      )
    );

    // Load Sectors - Updated to handle data property
    this.loadSectors$ = createEffect(() =>
      this.actions$.pipe(
        ofType(StocksActions.loadSectors),
        mergeMap(() =>
          this.stocksService.getSectors().pipe(
            map((response) => {
              // Extract sectors from response
              const sectors = response.data || [];
              return StocksActions.loadSectorsSuccess({ sectors });
            }),
            catchError((error) => of(StocksActions.loadSectorsFailure({ error: error.message })))
          )
        )
      )
    );

    // Load Markets - Updated to handle data property
    this.loadMarkets$ = createEffect(() =>
      this.actions$.pipe(
        ofType(StocksActions.loadMarkets),
        mergeMap(() =>
          this.stocksService.getMarkets().pipe(
            map((response) => {
              // Extract markets from response
              const markets = response.data.items || [];
              return StocksActions.loadMarketsSuccess({ markets });
            }),
            catchError((error) => of(StocksActions.loadMarketsFailure({ error: error.message })))
          )
        )
      )
    );

    // Load Industries - Updated to handle data property
    this.loadIndustries$ = createEffect(() =>
      this.actions$.pipe(
        ofType(StocksActions.loadIndustries),
        mergeMap(() =>
          this.stocksService.getIndustries().pipe(
            map((response) => {
              // Extract industries from response
              const industries = response.data.items || [];
              return StocksActions.loadIndustriesSuccess({ industries });
            }),
            catchError((error) => of(StocksActions.loadIndustriesFailure({ error: error.message })))
          )
        )
      )
    );

    // Load Template Properties
    this.loadTemplateProperties$ = createEffect(() =>
      this.actions$.pipe(
        ofType(StocksActions.loadTemplateProperties),
        mergeMap(({ templateId, category }) => {
          return this.stocksService.getTemplatePropertiesFromSettings(templateId, category).pipe(
            map((response) => {
              // Service already returns an array directly after transformation
              const properties = Array.isArray(response) ? response : [];
              return StocksActions.loadTemplatePropertiesSuccess({ properties });
            }),
            catchError((error) =>
              of(StocksActions.loadTemplatePropertiesFailure({ error: error.message }))
            )
          );
        })
      )
    );
  }
}
